
import { useState, useEffect, ChangeEvent } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { updateUserProfile } from "@/services/auth/userProfileService";
import { supabase } from "@/integrations/supabase/client";
import { companyFormSchema, CompanyFormValues } from "./schema";

export const useCompanyForm = () => {
  const { user, updateUserInContext } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoUrl, setLogoUrl] = useState(user?.companyLogo || "");
  const { toast } = useToast();
  
  // Initialize form with user data
  const form = useForm<CompanyFormValues>({
    resolver: zodResolver(companyFormSchema),
    defaultValues: {
      companyName: user?.companyName || "",
      document: user?.document || "",
      website: user?.website || "",
      phone: user?.phone || "",
      address: user?.address || "",
    },
  });

  // Load user data once when component mounts
  useEffect(() => {
    // Fetch user profile to get the complete profile with newly added fields
    const fetchProfile = async () => {
      try {
        if (user?.id) {
          const { data: profile, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', user.id)
            .single();
            
          if (error) {
            throw error;
          }
          
          if (profile) {
            form.reset({
              companyName: profile.company_name || "",
              document: profile.document || "",
              website: profile.website || "",
              phone: profile.phone || "",
              address: profile.address || "",
            });
            
            setLogoUrl(profile.company_logo || "");
          }
        }
      } catch (error) {
        console.error("Error loading profile data:", error);
      }
    };
    
    fetchProfile();
  }, [user?.id, form]);

  const handleLogoChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (file.size > 1024 * 1024) {
        toast({
          title: "Erro",
          description: "A imagem deve ter menos de 1MB",
          variant: "destructive",
        });
        return;
      }
      
      setLogoFile(file);
      // Create a preview
      const reader = new FileReader();
      reader.onload = () => {
        setLogoUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async (values: CompanyFormValues) => {
    if (!user) return;
    
    setIsLoading(true);
    
    try {
      // Prepare update data
      const updates = {
        companyName: values.companyName,
        companyLogo: logoFile || undefined,
        document: values.document,
        website: values.website,
        phone: values.phone,
        address: values.address,
      };
      
      // Update profile in Supabase
      const updatedUser = await updateUserProfile(user.id, updates);
      
      // Update local context if the update was successful
      if (updatedUser && updateUserInContext) {
        updateUserInContext(updatedUser);
      }
      
      toast({
        title: "Sucesso",
        description: "Informações da empresa atualizadas com sucesso",
      });
    } catch (error: any) {
      console.error("Error updating company info:", error);
      toast({
        title: "Erro",
        description: error.message || "Não foi possível atualizar as informações da empresa",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    form,
    isLoading,
    logoUrl,
    handleLogoChange,
    onSubmit,
    companyInitials: form.getValues().companyName?.substring(0, 2).toUpperCase() || "CO"
  };
};
